import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { resetAuthData as resetAuthDataSlice, setCompanyData as setCompanyDataSlice, setService as setServiceSlice } from "../../stores/modules/idp/authreducer";
import { fetchCompaniesService, fetchCompanyProfile as fetchCompanyProfileService } from '../../services/modules/idp/company';
import { resetCapSelfAssessmentData } from "../../stores/modules/cap/selfassessmentreducer";
import { Navbar, Button, Nav, Dropdown } from "react-bootstrap";
import Select from 'react-select';
import { faAlignLeft, faUserCircle, faSignOutAlt, faCogs } from "@fortawesome/free-solid-svg-icons";

const NavBar = ({ toggle }) => {
  // React pathname
  const location = useLocation();

  // Redux dispatch
  const dispatch = useDispatch();

  // React Router
  const navigate = useNavigate();
  
  // State variables
  const [selectedItem, setSelectedItem] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const inputValue = '';
  const appSlugs = ['crs', 'rs', 'cap', 'crq','vapt','soc'];
  const restrictedPaths = ['/cap/review-assessment', '/cap/rejectassessment', '/cap/assignmitigation', '/cap/mitigationsuccess', '/cap/mitigationreview', '/cap/rejectmitigation', '/cap/mitigationsuccess', '/cap/completereportdashboard', '/crs/threat', '/rs/threat', '/crs/category-threats-dashboard', 'rs/category-threats-dashboard', '/add-partnercompany', '/add-company', '/company-edit', '/add-user', '/profile', '/update-profile', '/update-password', '/myprofile','/vapt/vulnerability','/vapt/category-threats-dashboard','/vapt/dashboard'
    ,'/soc/dashboard','/soc/security-alerts','/soc/view-security-alert','/soc/sca-report','/soc/malware-data','/soc/vulnerability-data','/soc/fim-data','/soc/mitre-dashboard'
  ];
  
  // Redux selectors
  const authenticatedUserContainer = useSelector(state => state.auth.user);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const companyResultsContainer = useSelector((state) => state.company.companies.results);
  const serviceContainer = useSelector((state) => state.auth.service);
  
  // useEffect hook disables dropdown for restricted patches and fetches companies based on service
  useEffect(() => {
    const disableDropdown = restrictedPaths.some(url => location.pathname.includes(url));
    setDisabled(disableDropdown);
    const params = { "sortBy": "-createdAt" };
    let appslug = null;
    for (const slug of appSlugs) {
      if (location.pathname.includes(`/${slug}/`)) {
        if (slug === 'rs') {
          params['x-app-slug'] = 'rhymscan';
        } else {
          params['x-app-slug'] = slug;
        }
        appslug = (slug !== 'rs') ? slug : 'rhymscan';
        break;
      }
    }
    if (serviceContainer !== appslug) {
      dispatch(setServiceSlice(appslug));
      dispatch(fetchCompaniesService({ params }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  // useEffect hook sets the options to company names fetched based on conditions
  useEffect(() => {
    setOptions([]);
    let companylist = [];
    let loginCompanyexist = false;
    if (companyResultsContainer) {
      if (companyResultsContainer.length > 0) {
        // eslint-disable-next-line array-callback-return
        companyResultsContainer.map((item) => {
          if (authenticatedUserContainer.company.id === item.id) {
            loginCompanyexist = true;
          }
          let obj = {};
          obj.value = item.id;
          obj.label = item.name;
          obj.domain = item.domain;
          obj.partnercustomer = (item.customers && item.customers.length>0)? true:false;
          obj.soc_group = item.soc_group
          obj.soc_env = item.soc_env
          companylist.push(obj);
          if (item.customers && item.customers.length > 0) {
            // eslint-disable-next-line array-callback-return
            item.customers.map((e) => {
              if (authenticatedUserContainer.company.id === e.id) {
                loginCompanyexist = true;
              }
              let obj = {};
              obj.value = e.id;
              obj.label = e.name;
              obj.domain = e.domain;
              obj.partnercustomer = false;
              obj.soc_group = e.soc_group
              obj.soc_env = e.soc_env
              if ((e?.applications?.some(app => app.slug === serviceContainer)) || (serviceContainer === null) || (serviceContainer === '')) {
                companylist.push(obj);
              }
              else if ((serviceContainer === 'rs') && (e?.applications?.some(app => app.slug === 'rhymscan'))) {
                companylist.push(obj);
              }
            })
          }
        })
        if (loginCompanyexist === false) {
          let obj = {};
          obj.value = authenticatedUserContainer.company.id;
          obj.label = authenticatedUserContainer.company.name;
          obj.domain = authenticatedUserContainer.company.domain;
          obj.soc_group = authenticatedUserContainer.company.soc_group;
          obj.soc_env=authenticatedUserContainer.company.soc_env;
          obj.partnercustomer = (authenticatedUserContainer.company.customers.length>0)? true:false;
          companylist.unshift(obj);
        }
        let temp = new Set(companylist?.map(JSON.stringify));
        let uniqueOptions = Array.from(temp).map(JSON.parse)
        setOptions(uniqueOptions);
        fetchCompaniesData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyResultsContainer]);

  useEffect(() => {
    fetchCompaniesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer, options]);

  // Triggers company profile API if the company is not available in options and checks whether it has CAP service or not
  const fetchCompaniesData = async () => {
    if (options.length > 0) {
      if (selectedCompanyContainer.companyid !== undefined) {
        let isCompanyThere = false;
        options.forEach((e) => {
          if (e.value === selectedCompanyContainer.companyid) {
            setSelectedOption(e);
            isCompanyThere = true;
          }
        });
        if (isCompanyThere === false) {
          try {
            if (serviceContainer !== null) {
              const response = await fetchCompanyProfileService(selectedCompanyContainer.companyid);
              const hasService = response?.applications?.some(app => app.slug === serviceContainer);
              // If the company has CAP service,current option is set to company name and action is dispatched else current option is set to first company in options
              if (hasService) {
                let obj = {
                  value: selectedCompanyContainer.companyid,
                  label: selectedCompanyContainer.companyname,
                  domain: selectedCompanyContainer.domain,
                  partnercustomer: selectedCompanyContainer.partnercustomer,
                  soc_group:selectedCompanyContainer.soc_group,
                  soc_env:selectedCompanyContainer.soc_env,
                };
                options.push(obj);
                setSelectedOption(obj);
              } else {
                setSelectedOption(options[0]);
                let obj = {
                  companyid: options[0].value,
                  companyname: options[0].label,
                  domain: options[0].domain,
                  partnercustomer: options[0].partnercustomer,
                  soc_group:options[0].soc_group,
                  soc_env:options[0].soc_env
                };
                dispatch(setCompanyDataSlice(obj));
              }
            }
            else {
              let obj = {
                value: selectedCompanyContainer.companyid,
                label: selectedCompanyContainer.companyname,
                domain: selectedCompanyContainer.domain,
                partnercustomer: selectedCompanyContainer.partnercustomer
              };
              options.push(obj);
              setSelectedOption(obj);
            }

          } catch (error) {
            
          }
        }

      } else {
        setSelectedOption(options[0]);
      }
    }
  };
  
  // Handles navigation to profile,update password and logout respectively
  const handleItemClick = (itemName) => {
    setSelectedItem(itemName);
    switch (itemName) {
      case 'profile':
        navigate(`/myprofile`);
        break;
      case 'updatePassword':
        navigate('/update-password');
        break;
      case 'logout':
        dispatch(resetAuthDataSlice());
        dispatch(resetCapSelfAssessmentData());
        navigate('/login');
        break;
      default:
        break;
    }
  };
  
  // Handles change in company dropdown
  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    let obj = {};
    obj.companyid = selectedOption.value;
    obj.companyname = selectedOption.label;
    obj.soc_group = selectedOption.soc_group;
    obj.soc_env = selectedOption.soc_env
    const foundOption = options.find(option => option.value === selectedOption.value);
    if (foundOption) {
      obj.domain = foundOption.domain;
      obj.partnercustomer = foundOption.partnercustomer;
      obj.soc_group = foundOption.soc_group;
      obj.soc_env=foundOption.soc_env;
      
    }
    dispatch(setCompanyDataSlice(obj));
  };

  // Handles automatic search functionality for company dropdown
  const handleInputChange = (event, value, reason) => {
    if (event) {
      let params = {
        page: '1',
        limit: 10,
        sortBy: '-createdAt',
        search: event,
      };
      if ((event !== authenticatedUserContainer.company.name) && (event !== selectedCompanyContainer.companyname)) {
        dispatch(fetchCompaniesService({ params }));
      }
    }
  };
  
  // Filters options based on company name
  const filterOptions = (inputValue) => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <Navbar
      bg="light"
      className="navbar shadow-sm  mb-1 bg-white rounded"
      expand
    >
      <Button variant="outline-info" onClick={toggle}>
        <FontAwesomeIcon icon={faAlignLeft} />
      </Button>
      <Navbar.Toggle aria-controls="responsive-navbar-nav" />
      <Navbar.Collapse  className='justify-between'>
        {authenticatedUserContainer.roles[0].label !== "User" && (
          <div className="px-1 company-dropdown-container">
            <Select
              value={selectedOption}
              onChange={handleChange}
              onInputChange={handleInputChange}
              options={filterOptions(inputValue)}
              isSearchable={true}
              placeholder="Choose a company"
              isDisabled={disabled}
            />
          </div>
        )}

        <div className='company-name-container'>
          {authenticatedUserContainer?.company?.name}
        </div>
        <div className='user-name-container'>
          <span className="py-1" >
            {authenticatedUserContainer.name}
          </span>
        </div>
        <Nav className="ml-auto" navbar>

          <Dropdown alignRight>
            <Dropdown.Toggle variant="link">
              <FontAwesomeIcon icon={faUserCircle} size="lg" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-left dropdown-container-menu">
              <Dropdown.Item
                onClick={() => handleItemClick('profile')}
                className={selectedItem === 'profile' ? 'selected' : 'unSelected'}
              >
                <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                Profile
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                className={selectedItem === 'updatePassword' ? 'selected' : 'unSelected'}
                onClick={() => handleItemClick('updatePassword')}
              >
                <FontAwesomeIcon icon={faCogs} className="me-2" />
                Update password
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                className={selectedItem === 'logout' ? 'selected' : 'unSelected'}
                onClick={() => handleItemClick('logout')}
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Nav>
      </Navbar.Collapse>
    </Navbar>

  );
};

export default NavBar;
